import { useI18N } from "@sundae/react-hooks";
import { Avatar, Balance, HoverCard, Text } from "@sundae/ui-toolkit";
import { IHoverCardProps } from "@sundae/ui-toolkit/dist/types/components/organisms/HoverCard/HoverCard";
import { AssetAmount, IAssetAmountMetadata } from "@sundaeswap/asset";
import { SundaeUtils } from "@sundaeswap/core/utilities";
import { getShare } from "@sundaeswap/cpp";
import cx from "classnames";
import { FC, PropsWithChildren, useMemo, useState } from "react";

import { usePoolByLPAssetId } from "../../gql/hooks/pools.query";
import { IAssetMetaData } from "../../types/Asset.types";
import { getAssetLogo, getAssetName } from "../../utils/assets.utils";
import { getLPAssetBalances } from "../../utils/positions.utils";

export interface ILPHoverCardProps {
  lpAsset: AssetAmount;
  hoverCardProps?: IHoverCardProps;
}

const LpHoverCard: FC<PropsWithChildren<ILPHoverCardProps>> = ({
  children,
  lpAsset,
  hoverCardProps,
}) => {
  const { data, isLoading } = usePoolByLPAssetId(lpAsset.metadata.assetId);
  const [open, setOpen] = useState(false);
  const { currentLocale, t } = useI18N();
  const assetData = useMemo(() => {
    if (isLoading || !data?.quantityLP || !data?.quantityA || !data?.quantityB) {
      return null;
    }

    const share = getShare(lpAsset.amount, BigInt(data.quantityLP.quantity));
    const { assetA, assetB } = getLPAssetBalances(
      share.toNumber(),
      Number(data.quantityA.quantity),
      Number(data.quantityB.quantity)
    );
    return {
      assetA: new AssetAmount<IAssetAmountMetadata>(
        assetA,
        data.quantityA.asset as unknown as IAssetMetaData
      ),
      assetB: new AssetAmount<IAssetAmountMetadata>(
        assetB,
        data.quantityB.asset as unknown as IAssetMetaData
      ),
    };
  }, [data?.quantityA, data?.quantityB, data?.quantityLP]);

  const memoizedHoverCardItems = useMemo(() => {
    if (isLoading || !assetData) {
      return [];
    }

    const { assetA, assetB } = assetData;

    const isAdaAssetA = SundaeUtils.ADA_ASSET_IDS.includes(assetA?.metadata.id || "");
    const isAdaAssetB = SundaeUtils.ADA_ASSET_IDS.includes(assetB?.metadata.id || "");
    return [
      {
        id: 1,
        value: (
          <>
            <Avatar src={getAssetLogo(assetA.metadata as IAssetMetaData) || ""} size="sm" />
            <div className="flex items-center gap-1">
              <Balance
                abbreviate={false}
                locale={currentLocale}
                showSymbol={isAdaAssetA}
                size="sm"
                value={assetA}
              />
              {!isAdaAssetA && (
                <Text tag="span" size="sm">
                  {getAssetName(assetA.metadata)}
                </Text>
              )}
            </div>
          </>
        ),
      },
      {
        id: 2,
        value: (
          <>
            <Avatar src={getAssetLogo(assetB.metadata as IAssetMetaData) || ""} size="sm" />
            <div className="flex items-center gap-1">
              <Balance
                abbreviate={false}
                locale={currentLocale}
                showSymbol={isAdaAssetB}
                size="sm"
                value={assetB}
              />
              {!isAdaAssetB && (
                <Text tag="span" size="sm">
                  {getAssetName(assetB.metadata)}
                </Text>
              )}
            </div>
          </>
        ),
      },
    ];
  }, [assetData, isLoading]);

  if (!assetData) return <>{children}</>;

  return (
    <HoverCard
      {...hoverCardProps}
      open={open}
      primitiveProps={{
        content: { side: "top" },
        trigger: {
          ...hoverCardProps?.primitiveProps?.trigger,
          asChild: true,
          className: "w-fit flex items-center",
        },
      }}
      onOpenChange={setOpen}
      trigger={
        <div
          className={cx("relative flex cursor-help flex-wrap items-center gap-1 rounded-lg", {
            "motion-safe:animate-shadow-pulse": open,
          })}
        >
          {children}
        </div>
      }
    >
      <div className="relative z-10 flex h-full w-full items-center gap-2 motion-safe:animate-slide-up">
        <div className="grid w-full gap-4">
          <div className="grid gap-2">
            <Text tag="h2">{t("detail.widget.claim.currentValue", { ns: "taste-test" })}</Text>
          </div>

          <ul className="flex w-full flex-col gap-2">
            {memoizedHoverCardItems.map(({ id, value }, index) => (
              <li key={`${id}-${index}`} className="flex items-center gap-2">
                {value}
              </li>
            ))}
          </ul>
        </div>
      </div>
    </HoverCard>
  );
};

export default LpHoverCard;
